import { IOType } from '@erp/shared-types/node-graph';
import {
  Handle,
  Position,
  type NodeProps,
  useReactFlow,
  useStore,
} from '@xyflow/react';
import { IOSpan } from '../node-graph/io-span';
import { cn } from 'src/lib/utils';
import { Badge } from 'src/components/ui/badge';
import {
  ArrowRightIcon,
  CaretDownIcon,
  CopyIcon,
  EyeIcon,
  GlobeStandIcon,
  ShieldCheckIcon,
  TrashIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import {
  useContext,
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { T, useGT } from 'gt-react';
import { createPortal } from 'react-dom';
import { Separator } from '@/components/ui/separator';
import { useConnectionDrag } from 'src/pages/authenticated/workflows/context/ConnectionDragContext';
import {
  getConnectionDragHoverRejectionReason,
  isWorkflowSocketConnectionValid,
} from 'src/lib/workflows/socket-connection-validation';
import { useGuardrailPolicies } from '@/api/rpc/hooks/guardrail-policies/useGuardrailPolicies';
import { AuthContext } from '@/context/auth.context';
import { getCategoryStyle } from 'src/lib/workflows/categoryStyles';
import { getCategoryIcon } from 'src/lib/workflows/categoryIcons';
import {
  FLOW_IN_SOCKET_ID,
  FLOW_OUT_SOCKET_ID,
  FLOW_CONTROL_SOCKET_TYPE,
  ACTION_INPUT_STEP_TYPE,
  ACTION_OUTPUT_STEP_TYPE,
  isActionBoundaryStepType,
  getWorkflowGuardrailAssignmentConfigs,
  isArraySocketType,
} from '@erp/workflows';
import {
  isFlowConnectionValid,
  isValidConnection,
  isDownstream,
} from 'src/lib/workflows/graph-utils';
import {
  ACTION_TEMP_INPUT_SOCKET_ID,
  ACTION_TEMP_OUTPUT_SOCKET_ID,
} from 'src/lib/workflows/actionHandles';
import type { DraftState } from 'src/lib/workflows/workflowDraftOverlay';

interface MMGraphSocket {
  id: string;
  ioType: IOType;
  socketType?: string; // e.g. 'string', 'entity_reference', 'json', 'any', etc.
}

const stepIconStyles = cn('w-3 h-3');
const badgeIconStyles = cn('w-2 h-2');

interface NodeDataType {
  label: string;
  inputs: {
    [key: string]: MMGraphSocket;
  };
  outputs: {
    [key: string]: MMGraphSocket;
  };
  // Optional extras for UI treatments
  stepType?: string; // e.g. 'event_trigger'
  category?: string; // e.g. 'Data Access', 'Control Flow'
  _stepTypeData?: {
    name: string;
    category?: string;
    deprecated?: { reason: string; replacedBy?: string };
    [key: string]: unknown;
  };
  config?: Record<string, unknown>;
  emits?: string[]; // list of event names this step emits
  onViewAction?: (workflowDefinitionId: string, stepLabel: string) => void;
  onViewAgent?: (agentDefinitionId: string, stepLabel: string) => void;
  onViewEmailRules?: (emailRuleIds: string[], stepLabel: string) => void;
  onDuplicateNode?: (id: string) => void;
  // Validation state (injected by WorkflowEditor)
  editable?: boolean; // True only when rendered inside the workflow editor
  isFloating?: boolean; // Node has no connections at all
  unconnectedRequiredSockets?: Set<string>; // Socket IDs that are required but not connected
  // AI-49: draft preview state injected by the draft overlay
  __draftState?: DraftState;
  __flashHighlight?: boolean;
}

export function StepNode({ data, id, selected }: NodeProps) {
  const gt = useGT();
  const nodeData = data as unknown as NodeDataType;
  const [emitsOpen, setEmitsOpen] = useState(false);
  const [guardrailsOpen, setGuardrailsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const { deleteElements, getEdges, getNode, getNodes } = useReactFlow();
  const graphEdges = useStore((s) => s.edges);
  const { isDragging, dragInfo, isCompatibleHandle } = useConnectionDrag();

  const [wireHoverHint, setWireHoverHint] = useState<{
    message: string;
    left: number;
    top: number;
  } | null>(null);

  const wireHintShowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const wireHintDismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const wireHintLastKeyRef = useRef<{ key: string; at: number } | null>(null);

  const dismissWireHoverHint = useCallback(() => {
    if (wireHintShowTimerRef.current) {
      clearTimeout(wireHintShowTimerRef.current);
      wireHintShowTimerRef.current = null;
    }
    if (wireHintDismissTimerRef.current) {
      clearTimeout(wireHintDismissTimerRef.current);
      wireHintDismissTimerRef.current = null;
    }
    setWireHoverHint(null);
  }, []);

  useEffect(() => {
    if (!isDragging) {
      dismissWireHoverHint();
      wireHintLastKeyRef.current = null;
    }
  }, [isDragging, dismissWireHoverHint]);

  const { currentUser } = useContext(AuthContext);
  const organizationId = currentUser?.organizationId ?? '';
  const { data: guardrailPoliciesData } = useGuardrailPolicies(organizationId);
  const enabledAssignmentConfigs = useMemo(
    () =>
      getWorkflowGuardrailAssignmentConfigs(nodeData.config).filter(
        (assignment) => assignment.enabled,
      ),
    [nodeData.config],
  );
  const enabledAssignments = useMemo(
    () =>
      enabledAssignmentConfigs.map((assignment) => {
        const matchingPolicy = guardrailPoliciesData?.policies.find(
          (policy) => policy.id === assignment.guardrailDefinitionId,
        );
        return {
          id: assignment.guardrailDefinitionId,
          guardrailDefinitionId: assignment.guardrailDefinitionId,
          policyName: matchingPolicy?.name ?? assignment.guardrailDefinitionId,
        };
      }),
    [enabledAssignmentConfigs, guardrailPoliciesData?.policies],
  );
  const guardrailCount = enabledAssignments.length;
  const guardrailTooltip = useMemo(() => {
    if (guardrailCount === 0) return '';
    return enabledAssignments.map((a) => a.policyName).join('\n');
  }, [enabledAssignments, guardrailCount]);

  const allSockets = useMemo(
    () => [
      ...Object.entries(nodeData.inputs || {}),
      ...Object.entries(nodeData.outputs || {}),
    ],
    [nodeData.inputs, nodeData.outputs],
  );

  const isTrigger = useMemo(() => {
    const t = nodeData.stepType?.toLowerCase() ?? '';
    return t.endsWith('trigger') || t === ACTION_INPUT_STEP_TYPE;
  }, [nodeData.stepType]);

  const isDragOriginActionBoundary = useMemo(() => {
    if (!isDragging || !dragInfo?.nodeId) {
      return false;
    }

    const dragOriginNode = getNodes().find(
      (node) => node.id === dragInfo.nodeId,
    );
    const dragOriginData = dragOriginNode?.data as NodeDataType | undefined;
    return isActionBoundaryStepType(dragOriginData?.stepType ?? '');
  }, [dragInfo?.nodeId, getNodes, isDragging]);

  // Resolve category: from node data, step type metadata, or infer from trigger
  const category = useMemo(() => {
    return (
      nodeData.category ??
      nodeData._stepTypeData?.category ??
      (isTrigger ? 'Trigger' : undefined)
    );
  }, [nodeData.category, nodeData._stepTypeData?.category, isTrigger]);

  const catStyle = useMemo(() => getCategoryStyle(category), [category]);
  const CatIcon = useMemo(() => getCategoryIcon(category), [category]);

  const emits = nodeData.emits ?? [];
  const hasEmits = emits.length > 0;

  const workflowActionDefId =
    nodeData.stepType === 'workflow_action' &&
    typeof nodeData.config?.workflowDefinitionId === 'string'
      ? nodeData.config.workflowDefinitionId
      : undefined;
  const hasViewAction = !!workflowActionDefId && !!nodeData.onViewAction;

  const bgAgentDefId =
    nodeData.stepType === 'background_agent' &&
    typeof nodeData.config?.agentDefinitionId === 'string'
      ? nodeData.config.agentDefinitionId
      : undefined;
  const hasViewAgent = !!bgAgentDefId && !!nodeData.onViewAgent;

  const emailRuleIds =
    nodeData.stepType === 'email_rule_trigger' &&
    Array.isArray(nodeData.config?.emailRuleIds) &&
    (nodeData.config.emailRuleIds as string[]).length > 0
      ? (nodeData.config.emailRuleIds as string[])
      : undefined;
  const hasViewEmailRules = !!emailRuleIds && !!nodeData.onViewEmailRules;

  const isEditable = nodeData.editable ?? false;

  const scheduleIncompatibleHoverHint = useCallback(
    (
      hover: {
        nodeId: string;
        handleId: string;
        handleType: 'source' | 'target';
        socketType: string;
      },
      anchorEl: HTMLElement,
    ) => {
      if (!isEditable || !isDragging || !dragInfo) return;

      const reason = getConnectionDragHoverRejectionReason(
        {
          nodeId: dragInfo.nodeId,
          handleId: dragInfo.handleId,
          handleType: dragInfo.handleType,
          socketType: dragInfo.socketType,
        },
        hover,
        getNode,
        getEdges,
      );
      if (!reason) return;

      const key = `${dragInfo.nodeId}:${dragInfo.handleId}->${hover.nodeId}:${hover.handleId}`;

      if (wireHintShowTimerRef.current) {
        clearTimeout(wireHintShowTimerRef.current);
        wireHintShowTimerRef.current = null;
      }

      wireHintShowTimerRef.current = setTimeout(() => {
        wireHintShowTimerRef.current = null;
        const now = Date.now();
        const last = wireHintLastKeyRef.current;
        if (last && last.key === key && now - last.at < 2200) {
          return;
        }
        wireHintLastKeyRef.current = { key, at: now };

        const r = anchorEl.getBoundingClientRect();
        const centerX = r.left + r.width / 2;
        const margin = 12;
        const vw = typeof window !== 'undefined' ? window.innerWidth : 800;
        const left = Math.min(Math.max(centerX, margin), vw - margin);
        setWireHoverHint({
          message: reason,
          left,
          top: r.bottom + 6,
        });

        if (wireHintDismissTimerRef.current) {
          clearTimeout(wireHintDismissTimerRef.current);
        }
        wireHintDismissTimerRef.current = setTimeout(() => {
          wireHintDismissTimerRef.current = null;
          setWireHoverHint(null);
        }, 3800);
      }, 90);
    },
    [isEditable, isDragging, dragInfo, getNode, getEdges],
  );

  // Check if this node is compatible with the current drag
  // Include flow sockets in the check for non-trigger nodes
  const isNodeCompatible = useMemo(() => {
    if (!isDragging || !dragInfo?.nodeId) return true;

    const edges = getEdges();

    // Check if any of our typed handles are compatible
    const hasCompatibleTyped = allSockets.some(([, socket]) => {
      const handleType = socket.ioType === IOType.INPUT ? 'target' : 'source';
      const socketType = socket.socketType || 'any';

      // First check basic type compatibility
      if (!isCompatibleHandle(id, socket.id, handleType, socketType)) {
        return false;
      }

      // For output sockets: check graph position when dragging from flow input
      // (flow inputs accept any type, so we need to validate position)
      if (socket.ioType === IOType.OUTPUT && dragInfo.handleType === 'target') {
        // If this node is downstream of drag origin, output sockets are invalid
        if (isDownstream(dragInfo.nodeId, id, edges)) {
          return false;
        }
      }

      return true;
    });

    // Check flow input compatibility (only for non-triggers - triggers have no flow in)
    if (!isTrigger) {
      const flowInTypeCompatible = isCompatibleHandle(
        id,
        FLOW_IN_SOCKET_ID,
        'target',
        FLOW_CONTROL_SOCKET_TYPE,
      );
      const flowInCompatible =
        flowInTypeCompatible &&
        isFlowConnectionValid({
          thisNodeId: id,
          thisHandleType: 'target',
          dragOriginNodeId: dragInfo.nodeId,
          dragOriginHandleType: dragInfo.handleType,
          edges,
        });

      if (flowInCompatible) {
        return true;
      }
    }

    // Check flow output compatibility (ALL nodes including triggers have flow out)
    const flowOutTypeCompatible = isCompatibleHandle(
      id,
      FLOW_OUT_SOCKET_ID,
      'source',
      FLOW_CONTROL_SOCKET_TYPE,
    );
    const flowOutCompatible =
      flowOutTypeCompatible &&
      isFlowConnectionValid({
        thisNodeId: id,
        thisHandleType: 'source',
        dragOriginNodeId: dragInfo.nodeId,
        dragOriginHandleType: dragInfo.handleType,
        edges,
      });

    if (flowOutCompatible) {
      return true;
    }

    return hasCompatibleTyped;
  }, [
    isDragging,
    isCompatibleHandle,
    id,
    allSockets,
    isTrigger,
    dragInfo,
    getEdges,
  ]);

  // Check if there are any flow edges connected to this node (subscribe via
  // graphEdges so connects/disconnects recompute; getEdges alone is stable).
  const { hasFlowIn, hasFlowOut } = useMemo(() => {
    const flowInEdges = graphEdges.filter(
      (e) => e.target === id && e.targetHandle === FLOW_IN_SOCKET_ID,
    );
    const flowOutEdges = graphEdges.filter(
      (e) => e.source === id && e.sourceHandle === FLOW_OUT_SOCKET_ID,
    );
    return {
      hasFlowIn: flowInEdges.length > 0,
      hasFlowOut: flowOutEdges.length > 0,
    };
  }, [graphEdges, id]);

  // Check if flow handles are compatible during drag
  const isFlowInCompatible = useMemo(() => {
    if (!isDragging || !dragInfo?.nodeId) return true;

    // Basic type compatibility check
    const typeCompatible = isCompatibleHandle(
      id,
      FLOW_IN_SOCKET_ID,
      'target',
      FLOW_CONTROL_SOCKET_TYPE,
    );
    if (!typeCompatible) return false;

    // Check graph position to prevent cycles
    return isFlowConnectionValid({
      thisNodeId: id,
      thisHandleType: 'target',
      dragOriginNodeId: dragInfo.nodeId,
      dragOriginHandleType: dragInfo.handleType,
      edges: getEdges(),
    });
  }, [isDragging, isCompatibleHandle, id, dragInfo, getEdges]);

  const isFlowOutCompatible = useMemo(() => {
    if (!isDragging || !dragInfo?.nodeId) return true;

    // Basic type compatibility check
    const typeCompatible = isCompatibleHandle(
      id,
      FLOW_OUT_SOCKET_ID,
      'source',
      FLOW_CONTROL_SOCKET_TYPE,
    );
    if (!typeCompatible) return false;

    // Check graph position to prevent cycles
    return isFlowConnectionValid({
      thisNodeId: id,
      thisHandleType: 'source',
      dragOriginNodeId: dragInfo.nodeId,
      dragOriginHandleType: dragInfo.handleType,
      edges: getEdges(),
    });
  }, [isDragging, isCompatibleHandle, id, dragInfo, getEdges]);

  const showActionTempOutput =
    nodeData.stepType === ACTION_INPUT_STEP_TYPE &&
    isDragging &&
    dragInfo?.handleType === 'target' &&
    dragInfo.handleId !== FLOW_IN_SOCKET_ID &&
    !isDragOriginActionBoundary &&
    dragInfo.nodeId !== id;

  const showActionTempInput =
    nodeData.stepType === ACTION_OUTPUT_STEP_TYPE &&
    isDragging &&
    dragInfo?.handleType === 'source' &&
    dragInfo.handleId !== FLOW_OUT_SOCKET_ID &&
    !isDragOriginActionBoundary &&
    dragInfo.nodeId !== id;

  // Determine when to show flow sockets:
  // - Always show if connected
  // - Show on hover
  // - Show when dragging a flow control edge (for compatible handles)
  const showFlowIn =
    hasFlowIn || isHovered || (isDragging && isFlowInCompatible);
  const showFlowOut =
    hasFlowOut || isHovered || (isDragging && isFlowOutCompatible);

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      deleteElements({ nodes: [{ id }] });
    },
    [deleteElements, id],
  );

  const handleDuplicate = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      nodeData.onDuplicateNode?.(id);
    },
    [nodeData, id],
  );

  // Fanout silhouette: only when an array-typed data output is wired to a consumer.
  // (Array output alone does not imply fanout if nothing reads that socket.)
  const hasConnectedFanoutOutputs = useMemo(() => {
    const outputs = nodeData.outputs || {};
    for (const socket of Object.values(outputs)) {
      if (!socket.socketType || !isArraySocketType(socket.socketType)) {
        continue;
      }
      if (
        graphEdges.some((e) => e.source === id && e.sourceHandle === socket.id)
      ) {
        return true;
      }
    }
    return false;
  }, [graphEdges, id, nodeData.outputs]);

  // Get validation state from node data
  const isFloating = nodeData.isFloating ?? false;
  const unconnectedRequiredSockets = nodeData.unconnectedRequiredSockets;

  // AI-49: draft overlay visual treatment
  const draftState = nodeData.__draftState;
  const flashHighlight = nodeData.__flashHighlight ?? false;
  const draftWrapperClasses = cn(
    draftState === 'added' &&
      'border-dashed border-violet-500 dark:border-violet-400 opacity-90',
    draftState === 'modified' &&
      'ring-2 ring-violet-400 dark:ring-violet-500/60',
    draftState === 'removed' &&
      'border-dashed border-slate-400 pointer-events-none',
    flashHighlight && 'ring-2 ring-purple-400 animate-pulse',
  );

  return (
    <>
      <div className="relative">
        {/* Fanout silhouette cards */}
        {hasConnectedFanoutOutputs && (
          <>
            <div
              className={cn(
                'absolute inset-0 rounded-md border bg-gray-300 dark:bg-gray-600',
                'border-gray-400 dark:border-gray-500',
                'translate-x-2 translate-y-2',
              )}
            />
            <div
              className={cn(
                'absolute inset-0 rounded-md border bg-gray-200 dark:bg-gray-700',
                'border-gray-400 dark:border-gray-500',
                'translate-x-1 translate-y-1',
              )}
            />
          </>
        )}
        <div
          className={cn(
            'font-mono rounded-md min-w-[150px] bg-white dark:bg-gray-800 border shadow-sm transition-all relative group',
            selected
              ? cn('shadow-lg', catStyle.selectedBorder)
              : isFloating
                ? 'border-red-500 dark:border-red-400 ring-2 ring-red-500/20'
                : 'border-gray-300 dark:border-gray-600 hover:shadow-md',
            isDragging && !isNodeCompatible && 'opacity-40 pointer-events-none',
            draftWrapperClasses,
          )}
          style={
            draftState === 'removed'
              ? { animation: 'fade-removed 3s ease-in forwards' }
              : undefined
          }
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          title={
            isFloating
              ? gt('This node is not connected to the workflow')
              : undefined
          }
        >
          {/* Duplicate button - appears on hover in edit mode, right of delete */}
          {isEditable &&
            nodeData.onDuplicateNode &&
            (isHovered || selected) && (
              <button
                type="button"
                onClick={handleDuplicate}
                className={cn(
                  // Right of the delete badge (which sits at -right-2).
                  'absolute -top-2 -right-9 z-10 cursor-pointer',
                  'w-6 h-6 rounded-full',
                  'bg-stone-500 hover:bg-stone-600 text-white',
                  'flex items-center justify-center',
                  'shadow-md hover:shadow-lg',
                  'transition-all duration-150',
                  'opacity-0 group-hover:opacity-100',
                  selected && 'opacity-100',
                )}
                title={gt('Duplicate node', {
                  context: 'tooltip for duplicate node button',
                })}
              >
                <CopyIcon className="w-3 h-3" />
              </button>
            )}

          {/* Delete button - appears on hover in edit mode */}
          {isEditable && (isHovered || selected) && (
            <button
              type="button"
              onClick={handleDelete}
              className={cn(
                'absolute -top-2 -right-2 z-10 cursor-pointer',
                'w-6 h-6 rounded-full',
                'bg-red-500 hover:bg-red-600 text-white',
                'flex items-center justify-center',
                'shadow-md hover:shadow-lg',
                'transition-all duration-150',
                'opacity-0 group-hover:opacity-100',
                selected && 'opacity-100',
              )}
              title={gt('Delete node', {
                context: 'tooltip for delete node button',
              })}
            >
              <TrashIcon className="w-3 h-3" />
            </button>
          )}

          {/* Category icon badge in top-left */}
          <div className="absolute -top-3 -left-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full shadow p-1 z-10">
            <CatIcon className={cn(stepIconStyles, catStyle.iconColor)} />
          </div>

          {/* Deprecation badge */}
          {nodeData._stepTypeData?.deprecated && (
            <div
              className={cn(
                'absolute -top-2.5 left-6 z-20 flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wide shadow-sm border whitespace-nowrap',
                'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/50 dark:text-amber-200',
              )}
              title={nodeData._stepTypeData.deprecated.reason}
            >
              <WarningIcon className="w-2.5 h-2.5" />
              <T context="badge for deprecated workflow node">Deprecated</T>
            </div>
          )}

          {/* AI-49: draft state badge */}
          {draftState && draftState !== 'unchanged' && (
            <div
              data-testid="draft-state-badge"
              className={cn(
                'absolute -top-2.5 -right-2 z-20 px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wide shadow-sm border whitespace-nowrap',
                draftState === 'added' &&
                  'bg-violet-100 text-violet-700 border-violet-300 dark:bg-violet-900/50 dark:text-violet-200',
                draftState === 'modified' &&
                  'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/50 dark:text-amber-200',
                draftState === 'removed' &&
                  'bg-slate-200 text-slate-600 border-slate-300 dark:bg-slate-800 dark:text-slate-300',
              )}
            >
              {draftState === 'added' ? (
                <T context="badge for newly added workflow node">New</T>
              ) : draftState === 'modified' ? (
                <T context="badge for modified workflow node">Edited</T>
              ) : (
                <T context="badge for removed workflow node">Removed</T>
              )}
            </div>
          )}

          <div>
            <div className="relative">
              <div
                className={cn(
                  'rounded-t-sm px-2 py-1',
                  catStyle.header,
                  selected && 'opacity-90',
                )}
              >
                <div className="relative flex items-start justify-center">
                  <div className="min-w-0 text-center">
                    <h5
                      className="text-sm font-bold whitespace-nowrap text-ellipsis overflow-hidden"
                      title={nodeData.label}
                    >
                      {nodeData.label}
                    </h5>
                    {nodeData.stepType && (
                      <span className="text-[10px] opacity-50 font-normal">
                        {nodeData._stepTypeData?.name ??
                          nodeData.stepType
                            .replace(/_/g, ' ')
                            .replace(/\b\w/g, (c) => c.toUpperCase())}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Flow control handles - positioned on title bar sides */}
              {!isTrigger && (
                <Handle
                  className={cn(
                    '!min-w-2 !min-h-2',
                    '!bg-gray-300 dark:!bg-gray-500',
                    '!border !border-solid !border-gray-400 dark:!border-gray-400',
                    '!flex !items-center !justify-center',
                    !showFlowIn && '!opacity-0',
                    showFlowIn &&
                      !isFlowInCompatible &&
                      '!opacity-30 !cursor-not-allowed',
                  )}
                  type="target"
                  position={Position.Left}
                  id={FLOW_IN_SOCKET_ID}
                  title={gt('Wait For', {
                    context: 'flow control socket label for flow-in handle',
                  })}
                  isValidConnection={(connection) =>
                    isValidConnection(connection.source ?? '', id, getEdges())
                  }
                  onMouseEnter={(e) => {
                    if (!isEditable || !isDragging || !dragInfo) return;
                    if (isFlowInCompatible) {
                      dismissWireHoverHint();
                      return;
                    }
                    scheduleIncompatibleHoverHint(
                      {
                        nodeId: id,
                        handleId: FLOW_IN_SOCKET_ID,
                        handleType: 'target',
                        socketType: FLOW_CONTROL_SOCKET_TYPE,
                      },
                      e.currentTarget,
                    );
                  }}
                  onMouseLeave={dismissWireHoverHint}
                >
                  <ArrowRightIcon
                    className={cn(
                      'w-2.5 h-2.5 text-gray-600 dark:text-gray-300 pointer-events-none',
                      !showFlowIn && 'opacity-0',
                    )}
                  />
                </Handle>
              )}
              <Handle
                className={cn(
                  '!min-w-2 !min-h-2',
                  '!bg-gray-300 dark:!bg-gray-500',
                  '!border !border-solid !border-gray-400 dark:!border-gray-400',
                  '!flex !items-center !justify-center',
                  !showFlowOut && '!opacity-0',
                  showFlowOut &&
                    !isFlowOutCompatible &&
                    '!opacity-30 !cursor-not-allowed',
                )}
                type="source"
                position={Position.Right}
                id={FLOW_OUT_SOCKET_ID}
                title={gt('After Complete', {
                  context: 'flow control socket label for flow-out handle',
                })}
                isValidConnection={(connection) =>
                  isValidConnection(id, connection.target ?? '', getEdges())
                }
                onMouseEnter={(e) => {
                  if (!isEditable || !isDragging || !dragInfo) return;
                  if (isFlowOutCompatible) {
                    dismissWireHoverHint();
                    return;
                  }
                  scheduleIncompatibleHoverHint(
                    {
                      nodeId: id,
                      handleId: FLOW_OUT_SOCKET_ID,
                      handleType: 'source',
                      socketType: FLOW_CONTROL_SOCKET_TYPE,
                    },
                    e.currentTarget,
                  );
                }}
                onMouseLeave={dismissWireHoverHint}
              >
                <ArrowRightIcon
                  className={cn(
                    'w-2.5 h-2.5 text-gray-600 dark:text-gray-300 pointer-events-none',
                    !showFlowOut && 'opacity-0',
                  )}
                />
              </Handle>
            </div>
          </div>

          <div className="p-2">
            {allSockets.map(([socketName, socket]) => {
              // eslint-disable-next-line i18next/no-literal-string
              const targetType = 'target';
              // eslint-disable-next-line i18next/no-literal-string
              const sourceType = 'source';
              const handleType =
                socket.ioType === IOType.INPUT ? targetType : sourceType;
              // eslint-disable-next-line i18next/no-literal-string
              const socketType = socket.socketType || 'any';

              // Basic type compatibility check
              let isHandleCompatible =
                !isDragging ||
                isCompatibleHandle(id, socket.id, handleType, socketType);

              if (
                isHandleCompatible &&
                isDragging &&
                isActionBoundaryStepType(nodeData.stepType ?? '') &&
                isDragOriginActionBoundary
              ) {
                isHandleCompatible = false;
              }

              // For output sockets: if dragging from a flow input (which accepts any type),
              // also check graph position to prevent cycles
              if (
                isHandleCompatible &&
                isDragging &&
                socket.ioType === IOType.OUTPUT &&
                dragInfo?.handleType === 'target' &&
                dragInfo.nodeId
              ) {
                // If this node is downstream of the drag origin, output sockets are invalid
                if (isDownstream(dragInfo.nodeId, id, getEdges())) {
                  isHandleCompatible = false;
                }
              }

              // Use composite key to handle steps that have same socket.id for both input and output
              // (e.g., inboundEmail pass-through in extract-document-content step)
              const socketKey = `${handleType}-${socket.id}`;

              // Check if this input socket is required but not connected
              const isRequiredUnconnected =
                socket.ioType === IOType.INPUT &&
                unconnectedRequiredSockets?.has(socket.id);

              // Check if this is an array socket (supports multiple inputs via fan-in)
              const isArraySocket = isArraySocketType(socketType);

              return (
                <div
                  key={socketKey}
                  className={cn(
                    'relative flex items-center w-full',
                    socket.ioType === IOType.OUTPUT && 'justify-end',
                  )}
                >
                  <IOSpan text={socketName} ioType={socket.ioType} />
                  {/* Red asterisk for required unconnected input sockets */}
                  {isRequiredUnconnected && (
                    <span
                      className="text-red-500 font-bold ml-0.5 text-xs"
                      title={gt('Required input - must be connected', {
                        context: 'tooltip for required unconnected socket',
                      })}
                    >
                      *
                    </span>
                  )}
                  <Handle
                    className={cn(
                      '!min-w-2 !min-h-2 !top-1/2 !transform-none !-translate-y-1/2',
                      socket.ioType === IOType.INPUT ? '!-left-3' : '!-right-3',
                      // Grey out incompatible handles during drag, show not-allowed cursor
                      !isHandleCompatible && '!opacity-30 !cursor-not-allowed',
                      // Array sockets get a distinct style to indicate they accept multiple inputs
                      isArraySocket &&
                        socket.ioType === IOType.INPUT &&
                        '!border-2 !border-dashed !border-purple-500 dark:!border-purple-400',
                    )}
                    type={socket.ioType === IOType.INPUT ? 'target' : 'source'}
                    position={
                      socket.ioType === IOType.INPUT
                        ? Position.Left
                        : Position.Right
                    }
                    id={socket.id}
                    title={
                      isArraySocket && socket.ioType === IOType.INPUT
                        ? gt('{socketName} (accepts multiple connections)', {
                            socketName,
                          })
                        : socketName
                    }
                    isValidConnection={(connection) =>
                      isWorkflowSocketConnectionValid(
                        connection,
                        getNode,
                        getEdges,
                      )
                    }
                    onMouseEnter={(e) => {
                      if (!isEditable || !isDragging || !dragInfo) return;
                      if (isHandleCompatible) {
                        dismissWireHoverHint();
                        return;
                      }
                      scheduleIncompatibleHoverHint(
                        {
                          nodeId: id,
                          handleId: socket.id,
                          handleType,
                          socketType,
                        },
                        e.currentTarget,
                      );
                    }}
                    onMouseLeave={dismissWireHoverHint}
                  />
                </div>
              );
            })}

            {showActionTempInput && (
              <div className="relative mt-2 flex w-full items-center">
                <IOSpan
                  text={gt('New Input', {
                    context: 'label for new action input socket',
                  })}
                  ioType={IOType.INPUT}
                />
                <Handle
                  className="!-left-3 !top-1/2 !min-h-2 !min-w-2 !-translate-y-1/2 !transform-none !border-2 !border-dashed !border-sky-500"
                  type="target"
                  position={Position.Left}
                  id={ACTION_TEMP_INPUT_SOCKET_ID}
                  title={gt('Create action input', {
                    context: 'tooltip for creating action input socket',
                  })}
                  isValidConnection={(connection) =>
                    isValidConnection(connection.source ?? '', id, getEdges())
                  }
                />
              </div>
            )}

            {showActionTempOutput && (
              <div className="relative mt-2 flex w-full items-center justify-end">
                <IOSpan
                  text={gt('New Output', {
                    context: 'label for new action output socket',
                  })}
                  ioType={IOType.OUTPUT}
                />
                <Handle
                  className="!-right-3 !top-1/2 !min-h-2 !min-w-2 !-translate-y-1/2 !transform-none !border-2 !border-dashed !border-sky-500"
                  type="source"
                  position={Position.Right}
                  id={ACTION_TEMP_OUTPUT_SOCKET_ID}
                  title={gt('Create action output', {
                    context: 'tooltip for creating action output socket',
                  })}
                  isValidConnection={(connection) =>
                    isValidConnection(id, connection.target ?? '', getEdges())
                  }
                />
              </div>
            )}
          </div>

          {/* Emits drawer: slim badge row by default; expands on click */}
          {hasEmits && (
            <div className="overflow-hidden rounded-b-sm">
              <Separator className="px-0" />
              <div className="p-1 bg-accent">
                <button
                  type="button"
                  className="w-full cursor-pointer"
                  onClick={() => setEmitsOpen((v) => !v)}
                >
                  <div className="flex gap-2 items-center">
                    <CaretDownIcon
                      className={cn(
                        stepIconStyles,
                        'transition-transform duration-200',
                        emitsOpen ? '-rotate-90' : '',
                      )}
                    />
                    <Badge variant="outline" className="gap-1">
                      <GlobeStandIcon className={badgeIconStyles} />
                      {emits.length}
                    </Badge>
                  </div>
                </button>

                <div
                  className={cn(
                    'overflow-hidden transition-all',
                    emitsOpen ? 'max-h-64 mt-2' : 'max-h-0',
                  )}
                >
                  <div className="border-t border-dashed border-gray-300 dark:border-gray-600 pt-2 space-y-3">
                    {/* Section header with badge and rule */}
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="gap-1">
                        <GlobeStandIcon className={badgeIconStyles} />
                        <T context="badge label for event emits section">
                          Emits
                        </T>
                      </Badge>
                      <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600" />
                    </div>
                    <div className="space-y-1">
                      {emits.map((evt) => (
                        <div
                          key={evt}
                          className="text-[11px] px-2 py-1 rounded bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700"
                        >
                          {evt}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Guardrails drawer: shield badge row that expands to show policy names */}
          {guardrailCount > 0 && (
            <div className="overflow-hidden rounded-b-sm">
              <Separator className="px-0" />
              <div className="p-1 bg-accent">
                <button
                  type="button"
                  className="w-full cursor-pointer"
                  onClick={() => setGuardrailsOpen((v) => !v)}
                >
                  <div className="flex gap-2 items-center">
                    <CaretDownIcon
                      className={cn(
                        stepIconStyles,
                        'transition-transform duration-200',
                        guardrailsOpen ? '-rotate-90' : '',
                      )}
                    />
                    <Badge
                      variant="outline"
                      className="gap-1"
                      title={guardrailTooltip}
                    >
                      <ShieldCheckIcon className="w-[22px] h-[22px] text-blue-600 dark:text-blue-400" />
                      {guardrailCount}
                    </Badge>
                  </div>
                </button>

                <div
                  className={cn(
                    'overflow-hidden transition-all',
                    guardrailsOpen ? 'max-h-64 mt-2' : 'max-h-0',
                  )}
                >
                  <div className="border-t border-dashed border-gray-300 dark:border-gray-600 pt-2 space-y-3">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="gap-1">
                        <ShieldCheckIcon className="w-[22px] h-[22px] text-blue-600 dark:text-blue-400" />
                        <T context="badge label for guardrails section">
                          Guardrails
                        </T>
                      </Badge>
                      <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600" />
                    </div>
                    <div className="space-y-1">
                      {enabledAssignments.map((a) => (
                        <a
                          key={a.id}
                          href={`/settings/organization/guardrail-policies?policyId=${a.guardrailDefinitionId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block text-[11px] px-2 py-1 rounded bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 hover:border-blue-300 dark:hover:border-blue-600 transition-colors cursor-pointer"
                        >
                          {a.policyName}
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* View action: link to the underlying workflow action definition */}
          {hasViewAction && (
            <div className="overflow-hidden rounded-b-sm">
              <Separator className="px-0" />
              <div className="p-1 bg-accent">
                <button
                  type="button"
                  className="w-full cursor-pointer"
                  onClick={() => {
                    if (nodeData.onViewAction && workflowActionDefId) {
                      nodeData.onViewAction(
                        workflowActionDefId,
                        nodeData.label,
                      );
                    }
                  }}
                >
                  <div className="flex gap-2 items-center">
                    <EyeIcon className={stepIconStyles} />
                    <span className="text-[11px] font-medium">
                      <T context="button on workflow_action node to view the linked action">
                        View
                      </T>
                    </span>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* View agent: link to the background agent definition */}
          {hasViewAgent && (
            <div className="overflow-hidden rounded-b-sm">
              <Separator className="px-0" />
              <div className="p-1 bg-accent">
                <button
                  type="button"
                  className="w-full cursor-pointer"
                  onClick={() => {
                    if (nodeData.onViewAgent && bgAgentDefId) {
                      nodeData.onViewAgent(bgAgentDefId, nodeData.label);
                    }
                  }}
                >
                  <div className="flex gap-2 items-center">
                    <EyeIcon className={stepIconStyles} />
                    <span className="text-[11px] font-medium">
                      <T context="button on background_agent node to view the agent definition">
                        View
                      </T>
                    </span>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* View email rules: preview the linked email rules */}
          {hasViewEmailRules && (
            <div className="overflow-hidden rounded-b-sm">
              <Separator className="px-0" />
              <div className="p-1 bg-accent">
                <button
                  type="button"
                  className="w-full cursor-pointer"
                  onClick={() => {
                    if (nodeData.onViewEmailRules && emailRuleIds) {
                      nodeData.onViewEmailRules(emailRuleIds, nodeData.label);
                    }
                  }}
                >
                  <div className="flex gap-2 items-center">
                    <EyeIcon className={stepIconStyles} />
                    <span className="text-[11px] font-medium">
                      <T context="button on email_rule_trigger node to view the linked rules">
                        View
                      </T>
                    </span>
                  </div>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {wireHoverHint &&
        createPortal(
          <div
            className="pointer-events-none fixed z-[3000] max-w-[min(20rem,calc(100vw-1.5rem))]"
            style={{
              left: wireHoverHint.left,
              top: wireHoverHint.top,
              transform: 'translate(-50%, 0)',
            }}
          >
            <div
              role="status"
              className="rounded-md border border-border bg-popover px-3 py-2 text-popover-foreground shadow-lg"
            >
              <p className="text-xs font-semibold leading-tight">
                <T context="error message when workflow nodes cannot be connected">
                  Can't connect
                </T>
              </p>
              <p className="mt-1 text-xs text-muted-foreground leading-snug">
                {wireHoverHint.message}
              </p>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
